import React, { useEffect, useState } from "react";
import { api, formatApiError, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
const uuid = () => (crypto?.randomUUID?.() || Math.random().toString(36).slice(2));

const TABS = [
  { key: "payables", label: "PAYABLES" },
  { key: "batches", label: "PAYOUT BATCHES" },
  { key: "statements", label: "STATEMENTS" },
];

export default function PromoterFinancePage() {
  const { branchId, hasPerm } = useAuth();
  const [tab, setTab] = useState("payables");
  const canManage = hasPerm("promoter.commission.manage");
  const canPayout = hasPerm("promoter.payout.manage");

  return (
    <div className="p-6 lg:p-8" data-testid="promoter-finance-page">
      <div className="mb-6">
        <div className="tiny-label text-violet-400">Events → Commissions</div>
        <h1 className="mt-1 text-4xl font-extrabold tracking-tight">Promoter finance</h1>
        <p className="mt-1 text-zinc-500">Commission ledger, payout batches, and statements.</p>
      </div>
      <div className="flex gap-1 mb-6 border-b border-white/10">
        {TABS.map((t) => (
          <button key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`pf-tab-${t.key}`}
            className={`px-4 py-2 text-xs uppercase tracking-widest font-bold border-b-2 ${
              tab === t.key ? "border-violet-500 text-white"
                : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "payables" && <PayablesTab branchId={branchId} canManage={canManage}/>}
      {tab === "batches" && <BatchesTab branchId={branchId} canPayout={canPayout}/>}
      {tab === "statements" && <StatementsTab branchId={branchId}/>}
    </div>
  );
}

function PayablesTab({ branchId, canManage }) {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const params = branchId ? { branch_id: branchId } : {};
      const [{ data: e }, { data: s }] = await Promise.all([
        api.get("/promoter-commissions", { params }),
        api.get("/promoter-commissions/summary", { params }),
      ]);
      setEntries(e); setSummary(s);
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [branchId]);

  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/promoter-commissions/sync", {
        branch_id: branchId, idempotency_key: uuid() });
      toast.success(`Sync: ${data.entries_created} new entries`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="pf-payables-tab">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {["gross_earned", "available_payable", "reserved", "paid", "held", "disputed", "requires_repair"].map((k) => (
            <div key={k} className="panel p-3">
              <div className="tiny-label text-zinc-500">{k.replace(/_/g, " ")}</div>
              <div className="text-xl font-bold text-white">{peso(summary[k])}</div>
            </div>
          ))}
        </div>
      )}
      {canManage && (
        <button onClick={sync} disabled={busy} data-testid="pf-sync-btn"
                className="mb-4 h-9 px-4 rounded-md bg-violet-600 hover:bg-violet-500 text-white text-xs uppercase tracking-widest font-bold disabled:opacity-50">
          {busy ? "Syncing…" : "Sync missing commissions"}
        </button>
      )}
      <div className="panel overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>{["Date", "Promoter", "Source", "Ref", "Earned", "Reversed", "Paid", "Reserved", "Available", "Status"].map((h) => (
              <th key={h} className="p-2 text-left">{h}</th>))}</tr>
          </thead>
          <tbody data-testid="pf-entries-body">
            {entries.map((e) => (
              <tr key={e.id} data-testid={`pf-entry-${e.entry_id}`} className="border-t border-white/5">
                <td className="p-2 text-zinc-400">{(e.earned_at || "").slice(0, 10)}</td>
                <td className="p-2 text-white">{e.promoter_name}</td>
                <td className="p-2 text-zinc-300">{e.source_type}</td>
                <td className="p-2 text-zinc-500 mono">{(e.event_id || e.order_id || "").slice(-6)}</td>
                <td className="p-2 text-white">{peso(e.gross_commission)}</td>
                <td className="p-2 text-zinc-400">{peso(e.reversed_amount)}</td>
                <td className="p-2 text-zinc-400">{peso(e.paid_amount)}</td>
                <td className="p-2 text-amber-300">{peso(e.reserved_amount)}</td>
                <td className="p-2 text-emerald-300 font-bold">{peso(e.available_amount)}</td>
                <td className="p-2 text-xs uppercase tracking-widest">{e.status}</td>
              </tr>
            ))}
            {entries.length === 0 && (
              <tr><td colSpan={10} className="p-8 text-center text-zinc-500">No commission entries.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BatchesTab({ branchId, canPayout }) {
  const [batches, setBatches] = useState([]);
  const load = async () => {
    try {
      const { data } = await api.get("/promoter-payout-batches",
        { params: branchId ? { branch_id: branchId } : {} });
      setBatches(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [branchId]);

  return (
    <div data-testid="pf-batches-tab">
      <div className="panel overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>{["Batch #", "Promoter", "Method", "Total", "Status", "Created"].map((h) => (
              <th key={h} className="p-2 text-left">{h}</th>))}</tr>
          </thead>
          <tbody>
            {batches.map((b) => (
              <tr key={b.id} data-testid={`pf-batch-${b.batch_id}`} className="border-t border-white/5">
                <td className="p-2 text-white mono">{b.batch_number}</td>
                <td className="p-2 text-zinc-300">{b.promoter_name}</td>
                <td className="p-2 text-zinc-400">{b.payout_method}</td>
                <td className="p-2 text-white">{peso(b.total_amount)}</td>
                <td className="p-2 text-xs uppercase tracking-widest">{b.status}</td>
                <td className="p-2 text-zinc-500">{(b.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
            {batches.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-zinc-500">
                {canPayout ? "No payout batches yet." : "You do not have payout access."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatementsTab({ branchId }) {
  const [promoters, setPromoters] = useState([]);
  const [selected, setSelected] = useState(null);
  const [statement, setStatement] = useState(null);
  useEffect(() => {
    api.get("/promoters", { params: { active: true } })
      .then(({ data }) => setPromoters(data)).catch(() => setPromoters([]));
  }, []);
  const load = async (pid) => {
    setSelected(pid);
    try {
      const { data } = await api.get(`/promoters/${pid}/statement`,
        { params: branchId ? { branch_id: branchId } : {} });
      setStatement(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const downloadCsv = () => {
    if (!selected) return;
    const url = `${api.defaults.baseURL}/promoters/${selected}/statement.csv${branchId ? `?branch_id=${branchId}` : ""}`;
    window.open(url, "_blank");
  };
  return (
    <div data-testid="pf-statements-tab" className="space-y-4">
      <div className="flex gap-2 items-center">
        <select value={selected || ""} onChange={(e) => load(e.target.value)}
                data-testid="pf-statement-promoter"
                className="h-9 rounded-md bg-zinc-950 border border-white/10 text-sm text-white px-3">
          <option value="">Select promoter…</option>
          {promoters.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.code})</option>)}
        </select>
        {selected && (
          <button onClick={downloadCsv} data-testid="pf-download-csv"
                  className="h-9 px-4 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs uppercase tracking-widest font-bold">
            Download CSV
          </button>
        )}
      </div>
      {statement && (
        <div className="panel overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>{["Date", "Source", "Description", "Earned", "Reversed", "Paid", "Running", "Batch"].map((h) => (
                <th key={h} className="p-2 text-left">{h}</th>))}</tr>
            </thead>
            <tbody>
              {(statement.lines || []).map((ln, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="p-2 text-zinc-400">{(ln.date || "").slice(0, 10)}</td>
                  <td className="p-2 text-zinc-300">{ln.source}</td>
                  <td className="p-2 text-zinc-400">{ln.description}</td>
                  <td className="p-2 text-white">{peso(ln.earned)}</td>
                  <td className="p-2 text-zinc-400">{peso(ln.reversed)}</td>
                  <td className="p-2 text-zinc-400">{peso(ln.paid)}</td>
                  <td className="p-2 text-emerald-300 font-bold">{peso(ln.running_balance)}</td>
                  <td className="p-2 text-zinc-500">{ln.payout_batch_ref}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
